import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Globe, ImagePlus, Loader2, Play, Plus } from "lucide-react";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { cn } from "@/utils/helpers";
import { ALL_ASPECT_RATIOS, type AspectRatio, type IdeaMediaPick, type IdeaReference, type IdeaSource } from "@/types/contentCalendar.type";
import type { BrandMediaItem } from "@/types/brandMedia.type";
import type { Reference } from "@/types/reference.type";
import { BRAND_MEDIA_ACCEPT, BRAND_MEDIA_LIBRARY_LIMIT, useBrandMedia, useBrandMediaMutations } from "../../api/useBrandMedia";
import { KitButton } from "../../components/kit/Button";
import { KitInput } from "../../components/kit/Input";
import { KitModal } from "../../components/kit/Modal";
import PlatformGlyph, { hasGlyph, platformLabel } from "../../components/PlatformGlyph";
import {
  postReferenceSubmission,
  submissionsKey,
  useOrgId,
  useReferenceSubmissions,
  useReferences,
} from "../../references/api/useReferences";
import { useTrendDetail } from "../../trends/api/useTrends";

/**
 * Change media (Figma 08, 3 Ideas): Trend, References, Upload, None. Pick a
 * tile, then confirm. A file or a pasted link is saved to References first
 * ("It will show there for every idea") and comes back on the References tab,
 * picked and marked Just added. Write your own idea opens it to add a
 * reference: References and Upload only.
 *
 * The creative brief opens the same dialog for a shot's or a slide's picture
 * (`mode="visual"`), with Brand media first: a picture from the brand's
 * library is final media, anything else reads as a stand-in on the brief.
 */

export type MediaChoicePick = Exclude<IdeaMediaPick, { kind: "none" }>;

/** A pick with what a chip or an optimistic slide needs to show it. */
export interface MediaChoice {
  pick: MediaChoicePick;
  label: string;
  /** "Trend · Instagram", "Video · TikTok". */
  kindLine: string;
  platform: string;
  thumbnailUrl: string | null;
  videoUrl: string | null;
  imageUrl: string | null;
  /** The media's own shape, read off the file in the dialog. */
  ratio?: string | null;
}

/** "ASMR Spice Route" from "ASMR Spice Route: Cracking Open…": an idea's name in a sentence. */
export function shortIdeaName(title: string): string {
  const head = title.split(/[:—–|]/)[0]?.trim() || title.trim();
  return head.length > 48 ? `${head.slice(0, 47).trimEnd()}…` : head;
}

/** A choice as the slide draws a reference, for painting it before the server answers. */
export function referenceFromChoice(choice: MediaChoice): IdeaReference {
  const pick = choice.pick;
  const type = (ALL_ASPECT_RATIOS as string[]).includes(choice.ratio ?? "") ? (choice.ratio as AspectRatio) : "9:16";
  return {
    kind: pick.kind,
    type,
    mediaType: choice.videoUrl ? "video" : "image",
    platform: choice.platform,
    label: choice.label,
    videoUrl: choice.videoUrl,
    imageUrl: choice.imageUrl,
    pageUrl: null,
    thumbnailUrl: choice.thumbnailUrl,
    ...(pick.kind === "trend" ? { trendExampleId: pick.trendExampleId } : {}),
    ...(pick.kind === "reference" ? { referenceId: pick.referenceId } : {}),
    ...(pick.kind === "media" ? { mediaId: pick.mediaId } : {}),
    ...(pick.kind === "custom" ? { customMediaId: pick.customMediaId } : {}),
  };
}

type Tab = "library" | "trend" | "references" | "upload" | "none";
const TAB_LABEL: Record<Tab, string> = { library: "Brand media", trend: "Trend", references: "References", upload: "Upload", none: "None" };

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];

const RATIOS: Array<[string, number]> = [
  ["9:16", 9 / 16],
  ["3:4", 3 / 4],
  ["4:5", 4 / 5],
  ["1:1", 1],
  ["4:3", 4 / 3],
  ["16:9", 16 / 9],
  ["1.91:1", 1.91],
];

/** The nearest named ratio to a measured width over height. */
function ratioName(value: number | null): string | null {
  if (!value || !Number.isFinite(value)) return null;
  let best = RATIOS[0];
  for (const entry of RATIOS) {
    if (Math.abs(Math.log(entry[1] / value)) < Math.abs(Math.log(best[1] / value))) best = entry;
  }
  return best[0];
}

function savedDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return "Today";
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "");
}

function glyphKey(platform: string | null | undefined): string | null {
  const key = platform?.toUpperCase() ?? "";
  return key && hasGlyph(key) ? key : null;
}

function platformWord(platform: string | null | undefined): string | null {
  const key = glyphKey(platform);
  return key ? platformLabel(key) : null;
}

/** A reference the slide can show: our own mirror of a video, or a saved image. */
function servable(reference: Reference): boolean {
  if (reference.type === "video" || reference.type === "trend") return !!reference.videoUrl;
  return reference.type === "image" && !!reference.imageUrl;
}

function referenceWord(reference: Reference): string {
  if (reference.carousel) return "Carousel";
  if (reference.type === "trend") return "Trend";
  return reference.type === "image" ? "Image" : "Video";
}

function referenceChoice(reference: Reference): MediaChoice {
  const video = reference.type === "video" || reference.type === "trend";
  return {
    pick: { kind: "reference", referenceId: reference.id },
    label: reference.title,
    kindLine: [referenceWord(reference), platformWord(reference.platform)].filter(Boolean).join(" · "),
    platform: reference.platform ?? "",
    thumbnailUrl: reference.thumbnailUrl,
    videoUrl: video ? reference.videoUrl : null,
    imageUrl: reference.imageUrl,
  };
}

/** A picture from the brand's library: final media, no platform. */
function mediaChoice(item: BrandMediaItem): MediaChoice {
  return {
    pick: { kind: "media", mediaId: item.id },
    label: item.fileName,
    kindLine: "Brand media",
    platform: "",
    thumbnailUrl: item.thumbUrl ?? item.url,
    videoUrl: null,
    imageUrl: item.url ?? item.thumbUrl,
    ratio: item.width && item.height ? ratioName(item.width / item.height) : null,
  };
}

function choiceKey(pick: MediaChoicePick | null | undefined): string | null {
  if (!pick) return null;
  switch (pick.kind) {
    case "trend":
      return `trend:${pick.trendExampleId}`;
    case "reference":
      return `reference:${pick.referenceId}`;
    case "media":
      return `media:${pick.mediaId}`;
    case "custom":
      return `custom:${pick.customMediaId}`;
  }
}

/** The idea's current reference as a choice, when the dialog can name its row. */
function currentChoice(current: IdeaReference | null | undefined): MediaChoice | null {
  if (!current) return null;
  const base = {
    ratio: current.type,
    label: current.label,
    platform: current.platform,
    thumbnailUrl: current.thumbnailUrl,
    videoUrl: current.videoUrl,
    imageUrl: current.imageUrl ?? null,
  };
  if (current.trendExampleId) {
    return { ...base, pick: { kind: "trend", trendExampleId: current.trendExampleId }, kindLine: ["Trend", platformWord(current.platform)].filter(Boolean).join(" · ") };
  }
  if (current.referenceId) {
    return { ...base, pick: { kind: "reference", referenceId: current.referenceId }, kindLine: ["Reference", platformWord(current.platform)].filter(Boolean).join(" · ") };
  }
  if (current.customMediaId) {
    return { ...base, pick: { kind: "custom", customMediaId: current.customMediaId }, kindLine: ["Link", platformWord(current.platform)].filter(Boolean).join(" · ") };
  }
  if (current.mediaId) {
    return { ...base, pick: { kind: "media", mediaId: current.mediaId }, kindLine: "Brand media" };
  }
  return null;
}

/** Where the dialog opens: the tab holding the current media, else the first. */
function openingTab(mode: "change" | "add" | "visual", current: IdeaReference | null | undefined, citesTrend: boolean): Tab {
  if (mode === "add") return "references";
  if (mode === "visual") {
    if (current?.mediaId) return "library";
    if (current?.trendExampleId) return "trend";
    if (current?.referenceId || current?.customMediaId) return "references";
    return "library";
  }
  return citesTrend && !current?.referenceId ? "trend" : "references";
}

/**
 * One tile: the media at its own shape on a fixed height, the platform's mark,
 * a play mark on a video, the picked ring and check. Its ratio is read off the
 * file once it loads, so the meta line can say it.
 */
function MediaTile({
  choice,
  picked,
  onSelect,
  meta,
  tag,
  fallbackRatio,
}: {
  choice: MediaChoice;
  picked: boolean;
  /** With the ratio read off the file, when it has loaded. */
  onSelect: (ratio: string | null) => void;
  /** Builds the meta line once the ratio is known. */
  meta: (ratio: string | null) => ReactNode;
  tag?: ReactNode;
  fallbackRatio: number;
}) {
  const [ratio, setRatio] = useState<number | null>(null);
  const shape = ratio ?? fallbackRatio;
  // A library picture tiles from its 512px thumbnail; a reference's thumbnail
  // is the platform's own link (it rots), so its saved image comes first.
  const library = choice.pick.kind === "media";
  const still = library ? (choice.thumbnailUrl ?? choice.imageUrl) : (choice.imageUrl ?? choice.thumbnailUrl);
  const glyph = glyphKey(choice.platform);
  return (
    <div className="flex max-w-full flex-col gap-1.5" style={{ width: `min(100%, calc(var(--tile-h) * ${shape}))` }}>
      <button
        type="button"
        onClick={() => onSelect(ratioName(ratio))}
        aria-pressed={picked}
        aria-label={choice.label}
        className={cn(
          "relative block h-[var(--tile-h)] w-full overflow-hidden rounded-[8px] bg-co-recess outline-none transition-shadow",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
          picked ? "shadow-[0_0_0_2px_var(--co-accent)]" : "hover:shadow-[0_0_0_1px_var(--co-line2)]",
        )}
      >
        {choice.videoUrl ? (
          <video
            src={`${choice.videoUrl}#t=0.1`}
            poster={still ?? undefined}
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(event) => {
              const video = event.currentTarget;
              if (video.videoWidth && video.videoHeight) setRatio(video.videoWidth / video.videoHeight);
            }}
            className="size-full object-cover"
          />
        ) : still ? (
          <img
            src={still}
            alt=""
            onLoad={(event) => {
              const image = event.currentTarget;
              if (image.naturalWidth && image.naturalHeight) setRatio(image.naturalWidth / image.naturalHeight);
            }}
            className="size-full object-cover"
          />
        ) : null}
        {library ? null : (
          <span className="absolute left-2 top-2 grid size-[22px] place-items-center rounded-full bg-black/55 text-white">
            {glyph ? <PlatformGlyph platform={glyph} className="size-3" /> : <Globe className="size-3" strokeWidth={1.8} aria-hidden="true" />}
          </span>
        )}
        {choice.videoUrl ? (
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-9 place-items-center rounded-full border-2 border-white/90 text-white">
              <Play className="ml-0.5 size-3.5" fill="currentColor" strokeWidth={0} aria-hidden="true" />
            </span>
          </span>
        ) : null}
        {picked ? (
          <span className="absolute right-2 top-2 grid size-[18px] place-items-center rounded-[4px] bg-co-accent text-co-on-accent">
            <Check className="size-3" strokeWidth={2.6} aria-hidden="true" />
          </span>
        ) : null}
      </button>
      <p className="truncate text-[13px] leading-[18px] text-co-text">{choice.label}</p>
      <div className="font-mono text-[10px] uppercase leading-[13px] tracking-[0.16em] text-co-muted">{meta(ratioName(ratio))}</div>
      {tag}
    </div>
  );
}

/** A trend the idea cites: its lead playable clip, as a tile. */
function CitedTrendTile({ trendId, pickedKey, onSelect }: { trendId: string; pickedKey: string | null; onSelect: (choice: MediaChoice) => void }) {
  const { data } = useTrendDetail(trendId);
  const example = data?.examples.find((entry) => entry.videoUrl && !entry.videoBroken);
  if (!data || !example) return null;
  const choice: MediaChoice = {
    pick: { kind: "trend", trendExampleId: example.id },
    label: data.title,
    kindLine: ["Trend", platformWord(example.platform)].filter(Boolean).join(" · "),
    platform: example.platform,
    thumbnailUrl: example.thumbnailUrl,
    videoUrl: example.videoUrl,
    imageUrl: null,
  };
  return (
    <MediaTile
      choice={choice}
      picked={pickedKey === choiceKey(choice.pick)}
      onSelect={(ratio) => onSelect({ ...choice, ratio })}
      fallbackRatio={9 / 16}
      meta={(ratio) => trendMeta(example.platform, ratio)}
    />
  );
}

function trendMeta(platform: string, ratio: string | null) {
  const word = platform.toLowerCase() === "instagram" ? "Reel" : "Video";
  return `Trend · ${word}${ratio ? ` ${ratio}` : ""}`;
}

/** A file or link on its way into References. */
interface Pending {
  id: string;
  name: string;
  kind: "file" | "link";
  mediaKind: "video" | "image" | null;
  progress: number;
  submissionId: string | null;
  error: string | null;
}

export default function IdeaMediaDialog({
  brandId,
  isOpen,
  onClose,
  ideaLabel,
  plannedRatio,
  current = null,
  sources = [],
  mode = "change",
  title,
  description,
  ideaReference = null,
  onConfirm,
}: {
  brandId: string;
  isOpen: boolean;
  onClose: () => void;
  /** "Idea 3, ASMR Spice Route"; null when adding a reference to your own idea. */
  ideaLabel: string | null;
  /** The post's planned frame, "16:9". */
  plannedRatio: string;
  /** The idea's reference now (Change media), or the shot's picture now (visual). */
  current?: IdeaReference | null;
  /** The idea's evidence: the trends it cites fill the Trend tab. */
  sources?: IdeaSource[];
  mode?: "change" | "add" | "visual";
  /** Visual mode: the dialog's own title and line, naming the shot or slide. */
  title?: string;
  description?: string;
  /** Visual mode: the picked idea's own media, offered on the Trend tab. */
  ideaReference?: IdeaReference | null;
  /** The confirmed choice; null is None. */
  onConfirm: (choice: MediaChoice | null) => void;
}) {
  const visual = mode === "visual";
  const tabs: Tab[] =
    mode === "add"
      ? ["references", "upload"]
      : visual
        ? ["library", "trend", "references", "upload", ...(current ? (["none"] as const) : [])]
        : ["trend", "references", "upload", "none"];
  const initial = currentChoice(current);
  const citesTrend = !!current?.trendExampleId || sources.some((source) => source.type === "trend" && source.refId);
  // Opens where the media is now: the idea's trend, else References (the brief: its tab, else Brand media).
  const [tab, setTab] = useState<Tab>(() => openingTab(mode, current, citesTrend));
  const [choice, setChoice] = useState<MediaChoice | null>(initial);
  const [pending, setPending] = useState<Pending[]>([]);
  const [justAdded, setJustAdded] = useState<string[]>([]);
  const [link, setLink] = useState("");
  const [linkSaved, setLinkSaved] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const libraryInput = useRef<HTMLInputElement | null>(null);
  const orgId = useOrgId();
  const qc = useQueryClient();

  const { references, isLoading } = useReferences({ brandId, includeGlobal: true, limit: 60 }, { enabled: isOpen });
  // The library loads once its tab is open (and stays cached for the next shot).
  const libraryQuery = useBrandMedia(isOpen && visual && tab === "library" ? brandId : null, { limit: BRAND_MEDIA_LIBRARY_LIMIT });
  const { upload: libraryUpload } = useBrandMediaMutations(brandId);
  const [libraryAdded, setLibraryAdded] = useState<string | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const addToLibrary = async (file: File) => {
    setLibraryError(null);
    try {
      const created = await libraryUpload.mutateAsync(file);
      setLibraryAdded(created.id);
      setChoice(mediaChoice(created));
    } catch (error) {
      setLibraryError(getAiRequestErrorMessage(error, "That picture could not be added."));
    }
  };
  // A picture just added comes first, picked and marked Just added.
  const libraryListed = useMemo(() => {
    const items = libraryQuery.data?.media ?? [];
    const fresh = items.find((item) => item.id === libraryAdded);
    return fresh ? [fresh, ...items.filter((item) => item.id !== libraryAdded)] : items;
  }, [libraryAdded, libraryQuery.data]);
  const watching = pending.some((entry) => entry.submissionId && !entry.error);
  const { submissions } = useReferenceSubmissions({ enabled: isOpen && watching });

  // A submission that finished has a reference now: put it first, picked, on References.
  useEffect(() => {
    if (!watching) return;
    for (const entry of pending) {
      if (!entry.submissionId || entry.error) continue;
      const submission = submissions.find((row) => row.id === entry.submissionId);
      if (!submission) continue;
      if (submission.status === "failed") {
        setPending((list) => list.map((row) => (row.id === entry.id ? { ...row, error: submission.error || "It could not be saved." } : row)));
        continue;
      }
      if (submission.status !== "completed") continue;
      const made = references.find((reference) => reference.submissionId === submission.id);
      if (!made) continue;
      setPending((list) => list.filter((row) => row.id !== entry.id));
      setJustAdded((list) => [made.id, ...list.filter((id) => id !== made.id)]);
      if (servable(made)) {
        setChoice(referenceChoice(made));
        setTab("references");
      }
    }
  }, [pending, references, submissions, watching]);

  const listed = useMemo(() => {
    const shown = references.filter(servable);
    const fresh = justAdded.map((id) => shown.find((reference) => reference.id === id)).filter((r): r is Reference => !!r);
    return [...fresh, ...shown.filter((reference) => !justAdded.includes(reference.id))];
  }, [justAdded, references]);

  const pickedKey = choiceKey(choice?.pick);
  // The brief: the picked idea's own trend clip, when it is not the shot's already.
  const ideaTrend =
    visual && ideaReference?.trendExampleId && ideaReference.trendExampleId !== current?.trendExampleId
      ? currentChoice(ideaReference)
      : null;
  const trendIds = Array.from(
    new Set(sources.filter((source) => source.type === "trend" && source.refId).map((source) => source.refId as string)),
  ).filter((id) => id !== current?.trendId && id !== (ideaTrend ? ideaReference?.trendId : undefined));

  const submit = async (input: { input: string; uploads?: { s3Key: string; fileName: string; mimeType: string }[] }) => {
    const submission = await postReferenceSubmission(orgId, { ...input, brandId });
    void qc.invalidateQueries({ queryKey: submissionsKey(orgId) });
    return submission.id;
  };

  const addFiles = (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      const video = VIDEO_TYPES.includes(file.type);
      if (!video && !IMAGE_TYPES.includes(file.type)) continue;
      const id = crypto.randomUUID();
      setPending((list) => [...list, { id, name: file.name, kind: "file", mediaKind: video ? "video" : "image", progress: 0, submissionId: null, error: null }]);
      const patch = (next: Partial<Pending>) => setPending((list) => list.map((row) => (row.id === id ? { ...row, ...next } : row)));
      void (async () => {
        try {
          const res = await axiosInstance.post<{ uploadUrl: string; s3Key: string; mimeType: string }>(`/references/${orgId}/uploads`, {
            fileName: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
          });
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", res.data.uploadUrl);
            xhr.setRequestHeader("Content-Type", res.data.mimeType);
            xhr.upload.onprogress = (event) => event.lengthComputable && patch({ progress: Math.round((event.loaded / event.total) * 100) });
            xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed")));
            xhr.onerror = () => reject(new Error("Upload failed"));
            xhr.send(file);
          });
          patch({ progress: 100 });
          const submissionId = await submit({ input: "", uploads: [{ s3Key: res.data.s3Key, fileName: file.name, mimeType: file.type }] });
          patch({ submissionId });
        } catch (error) {
          patch({ error: getAiRequestErrorMessage(error, "Upload failed") });
        }
      })();
    }
  };

  const saveLink = async (raw: string) => {
    const url = raw.trim();
    if (!url) return;
    const id = crypto.randomUUID();
    setPending((list) => [...list, { id, name: url, kind: "link", mediaKind: null, progress: 100, submissionId: null, error: null }]);
    setLink("");
    try {
      const submissionId = await submit({ input: url });
      setLinkSaved(true);
      setPending((list) => list.map((row) => (row.id === id ? { ...row, submissionId } : row)));
    } catch (error) {
      setPending((list) => list.map((row) => (row.id === id ? { ...row, error: getAiRequestErrorMessage(error, "That link could not be saved.") } : row)));
    }
  };

  const keeping = !!choice && !!initial && choiceKey(initial.pick) === pickedKey;
  const confirmLabel = visual
    ? tab === "none"
      ? "Use no picture"
      : keeping
        ? "Keep this picture"
        : "Use this picture"
    : tab === "none"
      ? "Use no reference"
      : tab === "trend"
        ? keeping
          ? "Keep the trend"
          : "Use this trend"
        : tab === "upload" && pending.some((row) => row.kind === "file") && !choice
          ? "Use this file"
          : "Use this reference";
  const canConfirm =
    tab === "none" ||
    (!!choice &&
      (tab !== "trend" || choice.pick.kind === "trend") &&
      (tab !== "library" || choice.pick.kind === "media") &&
      !libraryUpload.isPending);

  const confirm = () => {
    if (tab === "none") onConfirm(null);
    else if (choice && !keeping) onConfirm(choice);
    onClose();
  };

  const trendRatioNote = (() => {
    if (tab !== "trend" || !choice || choice.pick.kind !== "trend") return null;
    return `The trend keeps its own shape. On this ${plannedRatio} idea it shows whole, marked “needs adapting” when it differs.`;
  })();
  const note =
    tab === "none"
      ? null
      : visual
        ? "A picture from Brand media is final. Anything else shows on the brief as a stand-in."
        : (trendRatioNote ?? `Each reference keeps its own shape. One that is not ${plannedRatio} like this idea is marked “needs adapting”.`);

  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={title ?? (mode === "add" ? "Add a reference" : "Change media")}
      description={
        description ??
        (mode === "add"
          ? "Pick from References or save a file or link to them. Creo reads it with your idea."
          : `${ideaLabel ? `${ideaLabel}. ` : ""}Only the reference changes. The idea, its hooks and why it works stay as they are.`)
      }
      modalClassName="flex max-h-[88dvh] max-w-[760px] flex-col"
    >
      <div role="tablist" aria-label="Where the media comes from" className="flex h-10 shrink-0 rounded-pill bg-co-tonal p-[3px]">
        {tabs.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={cn(
              "min-w-0 flex-1 rounded-pill text-[13.5px] outline-none transition-colors focus-visible:outline-2 focus-visible:outline-co-accent",
              tab === value ? "bg-co-hover font-semibold text-co-text shadow-[inset_0_0_0_1px_var(--co-line2)]" : "text-co-text2 hover:text-co-text",
            )}
          >
            {TAB_LABEL[value]}
          </button>
        ))}
      </div>

      {/* The -m / p pairs give focus rings (2px, outside the control) room inside the scroller, which clips them; layout is unchanged. */}
      <div className="-mx-1 -mb-1 mt-3 min-h-0 flex-1 overflow-y-auto pb-1 pl-1 pr-2 pt-1 [--tile-h:150px] sm:[--tile-h:210px]">
        {tab === "library" ? (
          libraryQuery.isLoading ? (
            <div className="flex flex-wrap gap-3" aria-hidden="true">
              {[1, 4 / 5, 1, 4 / 5, 1].map((shape, index) => (
                <span key={index} className="h-[var(--tile-h)] rounded-[8px] bg-co-tonal motion-safe:animate-pulse" style={{ width: `calc(var(--tile-h) * ${shape})` }} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => libraryInput.current?.click()}
                  disabled={libraryUpload.isPending}
                  className="flex h-[var(--tile-h)] w-[calc(var(--tile-h)*0.8)] shrink-0 flex-col items-center justify-center gap-2 rounded-[8px] border border-dashed border-co-faint text-[13.5px] text-co-text2 outline-none transition-colors hover:border-co-text2 focus-visible:outline-2 focus-visible:outline-co-accent disabled:cursor-progress"
                >
                  {libraryUpload.isPending ? (
                    <Loader2 className="size-4 animate-spin" strokeWidth={2} aria-hidden="true" />
                  ) : (
                    <ImagePlus className="size-4" strokeWidth={1.8} aria-hidden="true" />
                  )}
                  {libraryUpload.isPending ? "Adding…" : "Add a picture"}
                </button>
                {libraryListed.map((item) => {
                  const entry = mediaChoice(item);
                  const fresh = item.id === libraryAdded;
                  return (
                    <MediaTile
                      key={item.id}
                      choice={entry}
                      picked={pickedKey === choiceKey(entry.pick)}
                      onSelect={(ratio) => setChoice({ ...entry, ratio: ratio ?? entry.ratio })}
                      fallbackRatio={item.width && item.height ? item.width / item.height : 1}
                      meta={(ratio) => (
                        <>
                          <span className="block">Picture{ratio ? ` ${ratio}` : ""}</span>
                          <span className="block">{fresh ? "Today" : savedDay(item.createdAt)}</span>
                        </>
                      )}
                      tag={
                        fresh ? (
                          <span className="mt-0.5 inline-flex h-6 w-fit items-center rounded-[6px] bg-[color-mix(in_srgb,var(--co-pillar-2)_28%,transparent)] px-2.5 font-mono text-[11px] tracking-[0.12em] text-co-text">
                            Just added
                          </span>
                        ) : null
                      }
                    />
                  );
                })}
              </div>
              <input
                ref={libraryInput}
                type="file"
                accept={BRAND_MEDIA_ACCEPT}
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void addToLibrary(file);
                }}
              />
              {libraryError ? <p className="text-[13px] text-co-danger">{libraryError}</p> : null}
              {libraryListed.length === 0 && !libraryUpload.isPending ? (
                <p className="text-[14px] text-co-text2">Nothing in Brand media yet. Add a picture, or pick from References.</p>
              ) : null}
            </div>
          )
        ) : tab === "trend" ? (
          <div className="flex flex-wrap gap-3">
            {initial?.pick.kind === "trend" ? (
              <MediaTile
                choice={initial}
                picked={pickedKey === choiceKey(initial.pick)}
                onSelect={(ratio) => setChoice({ ...initial, ratio: ratio ?? initial.ratio })}
                fallbackRatio={9 / 16}
                meta={(ratio) => trendMeta(initial.platform, ratio)}
              />
            ) : null}
            {ideaTrend ? (
              <MediaTile
                choice={ideaTrend}
                picked={pickedKey === choiceKey(ideaTrend.pick)}
                onSelect={(ratio) => setChoice({ ...ideaTrend, ratio: ratio ?? ideaTrend.ratio })}
                fallbackRatio={9 / 16}
                meta={(ratio) => trendMeta(ideaTrend.platform, ratio)}
              />
            ) : null}
            {trendIds.map((trendId) => (
              <CitedTrendTile key={trendId} trendId={trendId} pickedKey={pickedKey} onSelect={setChoice} />
            ))}
            {initial?.pick.kind !== "trend" && !ideaTrend && trendIds.length === 0 ? (
              <p className="py-6 text-[14px] text-co-text2">
                {visual ? "Creo found no trend for this idea. Pick from Brand media or References, or upload one." : "Creo found no trend for this idea. Pick from References or upload one."}
              </p>
            ) : null}
          </div>
        ) : tab === "references" ? (
          isLoading ? (
            <div className="flex flex-wrap gap-3" aria-hidden="true">
              {[9 / 16, 4 / 5, 9 / 16, 1, 4 / 5].map((shape, index) => (
                <span key={index} className="h-[var(--tile-h)] rounded-[8px] bg-co-tonal motion-safe:animate-pulse" style={{ width: `calc(var(--tile-h) * ${shape})` }} />
              ))}
            </div>
          ) : listed.length === 0 ? (
            <p className="py-6 text-[14px] text-co-text2">No video or image references saved yet. Upload one, or paste a link.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {listed.map((reference) => {
                const entry = referenceChoice(reference);
                const fresh = justAdded.includes(reference.id);
                return (
                  <MediaTile
                    key={reference.id}
                    choice={entry}
                    picked={pickedKey === choiceKey(entry.pick)}
                    onSelect={(ratio) => setChoice({ ...entry, ratio })}
                    fallbackRatio={reference.type === "image" ? 4 / 5 : 9 / 16}
                    meta={(ratio) => (
                      <>
                        <span className="block">
                          {referenceWord(reference)}
                          {ratio ? ` ${ratio}` : ""}
                        </span>
                        <span className="block">{fresh ? "Today" : savedDay(reference.createdAt)}</span>
                      </>
                    )}
                    tag={
                      fresh ? (
                        <span className="mt-0.5 inline-flex h-6 w-fit items-center rounded-[6px] bg-[color-mix(in_srgb,var(--co-pillar-2)_28%,transparent)] px-2.5 font-mono text-[11px] tracking-[0.12em] text-co-text">
                          Just added
                        </span>
                      ) : null
                    }
                  />
                );
              })}
            </div>
          )
        ) : tab === "upload" ? (
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                addFiles(event.dataTransfer.files);
              }}
              className={cn(
                "flex h-[104px] flex-col items-center justify-center gap-2 rounded-card border border-dashed text-[13.5px] text-co-text2 outline-none transition-colors focus-visible:outline-2 focus-visible:outline-co-accent",
                dragging ? "border-co-accent bg-co-accent-dim" : "border-co-faint hover:border-co-text2",
              )}
            >
              <Plus className="size-4" strokeWidth={2} aria-hidden="true" />
              Drop the files here
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(",")}
              className="hidden"
              onChange={(event) => {
                if (event.target.files) addFiles(event.target.files);
                event.target.value = "";
              }}
            />
            {pending.map((row) => (
              <div key={row.id} className="flex min-h-[52px] items-center gap-3 rounded-card bg-co-tonal px-3 py-2">
                <span className="grid size-8 shrink-0 place-items-center rounded-[8px] bg-co-recess text-co-muted">
                  {row.kind === "link" ? <Globe className="size-4" strokeWidth={1.7} aria-hidden="true" /> : <Play className="size-3.5" strokeWidth={1.8} aria-hidden="true" />}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[13.5px] text-co-text">{row.name}</span>
                  <span className={cn("truncate text-[12px]", row.error ? "text-co-danger" : "text-co-muted")}>
                    {row.error
                      ? row.error
                      : row.progress < 100
                        ? `${row.mediaKind === "video" ? "Video" : "Picture"} · uploading, ${row.progress}%`
                        : "Saving to References…"}
                  </span>
                </span>
              </div>
            ))}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void saveLink(link);
              }}
            >
              <KitInput
                icon={Globe}
                aria-label="Paste a link"
                value={link}
                onChange={(event) => setLink(event.target.value)}
                onPaste={(event) => {
                  const text = event.clipboardData.getData("text").trim();
                  // A pasted link saves at once, as the Figma's field shows it saved.
                  if (/^https?:\/\//i.test(text)) {
                    event.preventDefault();
                    void saveLink(text);
                  }
                }}
                placeholder="Or paste a link to an Instagram, TikTok or YouTube post"
              />
            </form>
            {linkSaved ? <p className="text-[13px] text-co-text2">Saved to References. It will show there for every idea.</p> : null}
          </div>
        ) : (
          <p className="py-2 text-[14px] leading-[1.5] text-co-text2">
            {visual
              ? "The picture comes off, and the brief shows this one empty until someone picks again."
              : "The idea goes ahead on its words, with no picture or video from a reference."}
          </p>
        )}
      </div>

      {note ? <p className="mt-4 shrink-0 text-[13px] leading-[1.45] text-co-text2">{note}</p> : null}
      <div className="mt-5 flex shrink-0 items-center justify-end gap-3">
        <KitButton variant="ghost" onClick={onClose}>
          Cancel
        </KitButton>
        <KitButton onClick={confirm} isDisabled={!canConfirm}>
          {confirmLabel}
        </KitButton>
      </div>
    </KitModal>
  );
}
